import { setLeaf, setsIn, variableKind, type VariableKind } from '@agavi/blocks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TaskVariable } from '@/types/experience';

import { FlowPickerMenu, type PickerSection } from '@/components/task/flow-picker-menu';

/**
 * THE ONE WAY TO PICK A VARIABLE, for the rich-text toolbar and the formula field alike.
 *
 * Both were a flat wrap of every chip in the task. That is fine at four variables and
 * unmanageable at forty, which is what sets made ordinary: five stars with five properties each
 * is twenty-five chips in one undifferentiated block, all starting with the same few letters.
 *
 * So it reuses Flow's picker, which already answers this exact shape of problem for feedback
 * conditions — a closed set of options, grouped, too many to scan — and brings a search box,
 * grouped sections, arrow-key navigation, Escape and portalling with it. Writing a second one
 * would mean two keyboard implementations to keep honest.
 *
 * GROUPED BY WHAT A VARIABLE IS, then by set:
 *
 *   Constants / Randoms / Equations   the loose ones, alphabetical within each
 *   starA, starB, ...                 one section per set, members alphabetical
 *
 * A set's members appear ONLY under their set. Listing them in the type groups as well would
 * put every name on the screen twice, which is the problem this is here to solve.
 *
 * TASK AND EXPERIENCE VARIABLES ARE MERGED, deliberately. The distinction is about WHEN a value
 * is rolled — an experience variable rolls once per session, a task variable once per task — and
 * nothing about inserting a chip depends on that. A chip is a name either way, the names are
 * unique across the two scopes already, and splitting the menu in half by a property that does
 * not change the choice would double the headings for no decision.
 */

/** Constants first (the givens), then randoms, then the equations built on them — the panel's order. */
const KIND_ORDER: VariableKind[] = ['constant', 'random', 'equation'];

const KIND_HEADER: Record<VariableKind, string> = {
  constant: 'blocks.toolbar.varGroupConstants',
  random: 'blocks.toolbar.varGroupRandoms',
  equation: 'blocks.toolbar.varGroupEquations',
};

/** Alphabetical, case-insensitively, so `Mass` and `mass` do not sort into separate runs. */
const byName = (a: TaskVariable, b: TaskVariable) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });

export function variableSections(variables: TaskVariable[], t: (k: string) => string): PickerSection[] {
  const named = variables.filter(v => v.name);
  const loose = named.filter(v => !v.set);

  const sections: PickerSection[] = [];

  for (const kind of KIND_ORDER) {
    const inKind = loose.filter(v => variableKind(v) === kind).sort(byName);
    if (inKind.length === 0) continue;
    sections.push({
      header: t(KIND_HEADER[kind]),
      options: inKind.map(v => ({ key: v.name, text: v.name })),
    });
  }

  // One section per set, in the order the sets were made, with members alphabetical inside.
  // The header is the set's own name rather than a category word: `starA` IS the category.
  for (const set of setsIn(named)) {
    const members = named.filter(v => v.set === set).sort(byName);
    if (members.length === 0) continue;
    sections.push({
      header: set,
      // A set's header is the teacher's own identifier, not a category word: `starA` upper-cased
      // to `STARA` cannot be typed back, and the members below it read `starA_mass`.
      headerIsName: true,
      // The LEAF is what shows, because the header above already says the set. What is inserted
      // is still the full composed name — the identity never changes, only the label.
      options: members.map(v => ({ key: v.name, text: setLeaf(v) })),
    });
  }

  return sections;
}

export function VariablePickerMenu({ variables, onChoose, ariaLabel, renderTrigger, handleRef, onDismiss, className }: {
  variables: TaskVariable[];
  /** The variable's full name, composed, exactly as it is stored. */
  onChoose: (name: string) => void;
  ariaLabel: string;
  renderTrigger: Parameters<typeof FlowPickerMenu>[0]['renderTrigger'];
  /** Opens the picker at the caret — see `FlowPickerMenu`'s own note, and the `{` shortcut. */
  handleRef?: Parameters<typeof FlowPickerMenu>[0]['handleRef'];
  onDismiss?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const sections = useMemo(() => variableSections(variables, t), [variables, t]);

  return (
    <FlowPickerMenu
      sections={sections}
      onChoose={onChoose}
      ariaLabel={ariaLabel}
      filterPlaceholder={t('blocks.toolbar.filterVariables')}
      noMatchesLabel={t('blocks.toolbar.noVariablesMatch')}
      renderTrigger={renderTrigger}
      handleRef={handleRef}
      onDismiss={onDismiss}
      className={className}
    />
  );
}
